<template>
  <el-drawer
    :visible="show"
    :append-to-body="true"
    destroy-on-close
    size="600px"
    @close="$emit('close')"
  >
    <div class="content-wrapper">
      <p class="text-primary-100 font-medium" style="font-size: 24px;">{{ displayData.title }}</p>

      <!-- 基本資訊 -->
      <div class="flex flex-col" style="margin-top: 16px; gap: 8px">
        <div class="flex items-center" style="gap: 4px">
          <span>{{ displayData.userName }}</span>
          <span>{{ displayData.userPhone }}</span>
        </div>

        <div class="flex items-center justify-between">
          <p>時間：{{ displayData.sellAt }}</p>
          <p>銷售紀錄編號：{{ displayData.orderNo }}</p>
        </div>
      </div>

      <!-- 商品資訊 -->
      <div>
        <div v-for="salesUnit in displaySalesUnitGroupItems" :key="salesUnit.id" class="sales-unit-group">
          <div class="sales-unit">
            {{ salesUnit.name }}
          </div>
          <div class="flex flex-col" style="gap: 12px">
            <div v-for="item in salesUnit.items" :key="item.id" class="flex flex-col" style="gap: 6px">
              <div class="row">
                <p>{{ item.name }} <span>x{{ item.quantity }}</span></p>
                <p class="font-medium flex-shrink-0" style="font-size: 18px;">$ {{ item.totalPrice }}</p>
              </div>

              <el-collapse-transition>
                <div class="flex flex-col" style="gap: 6px">
                  <div v-for="discount in item.discounts" :key="discount.id" class="row text-sm text-gray-60">
                    <p>{{ discount.name }}</p>
                    <p class="flex-shrink-0">-$ {{ discount.savePrice }}</p>
                  </div>
                </div>
              </el-collapse-transition>
            </div>
          </div>
        </div>
      </div>

      <!-- 整單折扣 -->
      <div v-if="showControl.block.orderDiscount">
        <el-divider />
        <div class="flex flex-col" style="gap: 12px">
          <div class="row font-medium">
            <p>整單折扣</p>
            <p class="flex-shrink-0" style="font-size: 18px;">-$ {{ displayData.totalOrderDiscountPrice }}</p>
          </div>

          <div class="flex flex-col" style="gap: 6px">
            <div v-for="discount in displayOrderDiscounts" :key="discount.id" class="row text-sm text-gray-60">
              <p>{{ discount.name }}</p>
              <p class="flex-shrink-0">-$ {{ discount.savePrice }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showControl.block.appointmentInfo">
        <el-divider />
        <div class="flex flex-col" style="gap: 12px">
          <div v-for="item in displayAppointmentInfo" :key="item.type" class="row">
            <p>{{ item.name }}</p>
            <p class="font-medium flex-shrink-0" style="font-size: 18px;">{{ item.displayValue }}</p>
          </div>
        </div>
      </div>

      <!-- 付款方式 -->
      <div>
        <el-divider />
        <FlexLayout col :gap="12">
          <div class="row font-medium">
            <p>總額</p>
            <p class="flex-shrink-0">{{ displayData.orderTotalPrice }}</p>
          </div>

          <FlexLayout col :gap="12">
            <div v-for="payment in displayOrderPayments" :key="payment.id" class="row">
              <p>{{ payment.name }}</p>
              <p class="flex-shrink-0">$ {{ payment.amount }}</p>
            </div>
          </FlexLayout>

          <div class="row font-medium">
            <p>認列金額</p>
            <p class="flex-shrink-0">{{ displayData.recognizedAmount }}</p>
          </div>
        </FlexLayout>
      </div>

      <!-- 發票資訊 -->
      <SalesRecordInvoiceSection
        v-if="showControl.block.invoiceInfo"
        :sales-record="data"
      />

      <!-- 本次新增 -->
      <div v-if="showControl.block.addBenefits">
        <el-divider />
        <div class="flex flex-col" style="gap: 12px">
          <div>
            <p class="font-medium">本次新增</p>
          </div>

          <div class="flex flex-col" style="gap: 12px">
            <div v-for="item in displayAddBenefits" :key="item.id" class="row">
              <p>{{ item.name }}</p>
              <p class="flex-shrink-0">{{ item.prefix }} {{ item.quantity }} {{ item.suffix }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 本次扣除 -->
      <div v-if="showControl.block.usedBenefits">
        <el-divider />
        <div class="flex flex-col" style="gap: 12px">
          <div>
            <p class="font-medium">本次扣除</p>
          </div>

          <div class="flex flex-col" style="gap: 12px">
            <div v-for="item in displayUsedBenefits" :key="item.id" class="row">
              <p>{{ item.name }}</p>
              <p class="flex-shrink-0">{{ item.prefix }} {{ item.quantity }} {{ item.suffix }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 獲得回饋 -->
      <div>
        <el-divider />
        <div class="flex flex-col" style="gap: 12px;">
          <div v-if="showControl.block.rewards">
            <p class="font-medium">本次可獲得回饋</p>
          </div>

          <div v-if="showControl.block.rewards" class="flex flex-col" style="gap: 12px;">
            <div v-for="(reward, groupKey) in displayRewards" :key="groupKey" class="row">
              <p>{{ reward.name }}</p>
              <p class="flex-shrink-0">{{ reward.displayValue }}</p>
            </div>
          </div>

          <div class="row items-start" style="gap: 70px; align-items: start;">
            <div class="flex items-center" style="gap: 8px">
              <p class="flex-shrink-0">備註</p>
              <div
                v-if="showControl.btn.createNote"
                class="text-primary-100 underline cursor-pointer"
                @click="modal.noteEdit = true"
              >
                填寫備註
              </div>
            </div>
            <div class="flex items-start" style="gap: 8px">
              <div class="break-all">
                {{ displayData.note || '-' }}
              </div>
              <div
                v-if="showControl.btn.editNote"
                class="text-primary-100 underline cursor-pointer flex-shrink-0"
                @click="modal.noteEdit = true"
              >
                編輯
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 捷運點發放紀錄 -->
      <div v-if="showControl.block.eventAward">
        <el-divider />
        <FlexLayout col :gap="12">
          <div>
            <p class="font-medium">{{ taipeiMetroPoint.name }}發放紀錄</p>
          </div>

          <!-- 提示文字 (無紀錄時) -->
          <div
            v-if="eventAwardDisplayStatus.showHintMessage"
            :class="eventAwardDisplayStatus.hintTextColor"
          >
            {{ eventAwardDisplayStatus.hintMessage }}
          </div>

          <!-- 發放紀錄列表 -->
          <template v-if="eventAwardDisplayStatus.showRecords">
            <div
              v-for="(record, idx) in eventPlaybookAwardRecords"
              :key="record.id"
            >
              <BobDisplayForm
                :fields="generateDisplayFields(record)"
                :config="{ rowGap: 0 }"
                :label-classes="['text-gray-100', 'font-normal']"
                :content-classes="['w-full', 'items-end', 'font-medium']"
              />
              <el-divider v-if="idx < eventPlaybookAwardRecords.length - 1" />
            </div>
          </template>
        </FlexLayout>
      </div>

      <!-- Footer -->
      <div>
        <el-divider />
        <div class="flex flex-col" style="gap: 24px;">
          <div class="row">
            <p>新增紀錄操作單位</p>
            <p>{{ displayData.creatorName }}</p>
          </div>
          <div class="row">
            <p>建立管道</p>
            <p>{{ displayData.createOrigin }}</p>
          </div>
          <div v-if="isOrg" class="row">
            <p>建立分店</p>
            <p>{{ displayData.shopName }}</p>
          </div>
          <div class="row">
            <p>銷售紀錄建立時間</p>
            <p>{{ displayData.createdAt }}</p>
          </div>
        </div>
      </div>

      <!-- QRCode -->
      <div v-if="showControl.block.qrcode" class="flex flex-col" style="gap: 16px; margin-top: 16px;">
        <p class="text-primary-100 font-medium">交易 QRcode</p>
        <div v-if="showControl.paymentQRcode" class="flex justify-center">
          <img :src="paymentQrcode" alt="QR Code" style="width: 179px;">
        </div>
        <p v-if="showControl.qrcodeInvalid">交易逾時 QRcode 已作廢</p>
      </div>

      <div class="flex justify-end" style="margin-top: 80px;">
        <BaseElButton style="width: 100px" plain @click="$emit('close')">
          返回
        </BaseElButton>
        <BaseElButton
          v-if="showControl.btn.void"
          style="width: 100px"
          type="primary"
          @click="onOrderVoid"
        >
          作廢
        </BaseElButton>
      </div>
    </div>

    <EditSalesRecordNoteModal
      v-if="modal.noteEdit"
      :data="data"
      @close="modal.noteEdit = false"
      @refresh="onRefresh"
    />
    <SalesRecordCancelModal
      v-if="modal.void"
      :record="data"
      :configData="configData"
      @close="modal.void = false"
      @refresh="onVoidRefresh"
    />
  </el-drawer>
</template>

<script>
import { paymentOptionsConfig } from '@/config/payment'
import { formatDate } from '@/utils/date'
import { filter, includes, uniqueId, map, forEach, get, isEmpty, find, reduce } from 'lodash'

import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, reactive, ref } from 'vue'
import EditSalesRecordNoteModal from './EditSalesRecordNoteModal.vue'
import { useQRCode } from '@vueuse/integrations/useQRCode'
import SalesRecordCancelModal from './SalesRecordCancelModal.vue'
import { salesRecordCreateOriginDeviceConfig } from '@/config/sales'
import { sendStatusConfig } from '@/config/marketing'
import { useShop } from '@/use/shop'
import { useShopPoint } from '@/use/useShopPoint'
import { formatBenefitChangeLogs } from '@/use/useSalesPOS'
import { FindOrgEventPlaybookAwardRecord } from '@/api/OrgSalesRecord'
import { GetMemberTaipeiMetroMissionStatus } from '@/api/taipeiMetro'
import { usePermissions } from '@/use/permissions'
import { useInvoiceConfig } from '@/use/useInvoiceConfig'
import BobDisplayForm from '@/components/BobDisplayForm.vue'
import SalesRecordInvoiceSection from './SalesRecordInvoiceSection.vue'

export default defineComponent({
  name: 'SalesRecordDetailDrawer',
  components: {
    EditSalesRecordNoteModal,
    SalesRecordCancelModal,
    BobDisplayForm,
    SalesRecordInvoiceSection,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isOrg: {
      type: Boolean,
      default: false,
    },
  },
  setup (props, { emit }) {
    const configData = inject('configData')
    const { defaultShopPoint, shopId, shopConfig } = useShop()
    const { getShopPointData, shopPointList, isTaipeiMetroPoint } = useShopPoint()
    const { checkAction } = usePermissions()
    const { config: invoiceConfig, fetchConfig: fetchInvoiceConfig, salesConfig, fetchSalesConfig } = useInvoiceConfig()
    const show = ref(false)
    const modal = reactive({
      noteEdit: false,
      void: false,
    })
    const paymentQrcode = useQRCode(get(props.data, 'SalesPaymentOrder.code'))
    const eventPlaybookAwardRecords = ref([])
    const displayData = computed(() => {
      const sellAt = get(props.data, 'sellAt')
      const recognized = get(props.data, 'recognizedAmount')
      const data = {
        title: '銷售紀錄',
        createdAt: formatDate(get(props.data, 'createdAt')),
        sellAt: sellAt ? formatDate(get(props.data, 'sellAt')) : '-',
        orderNo: get(props.data, 'orderNo'),
        note: get(props.data, 'note'),
        recognizedAmount: recognized === undefined || recognized === null ? '-' : recognized,
      }

      const totalItemDiscountPrice = get(props.data, 'totalItemDiscountPrice')
      const totalDiscountPrice = get(props.data, 'totalDiscountPrice')
      const totalItemsPrice = get(props.data, 'totalItemsPrice')

      data.totalOrderDiscountPrice = totalDiscountPrice - totalItemDiscountPrice

      data.orderTotalPrice = totalItemsPrice - totalDiscountPrice

      // 建立管道
      const origin = get(props.data, 'origin')
      let displayOrigin
      if (origin) {
        if (origin === 'admin') displayOrigin = '後台'
        else if (origin === 'client') displayOrigin = '顧客'
        else if (origin === 'admin_past') displayOrigin = '後台跨日補登'
      }
      const originDevice = get(props.data, 'originDevice')

      data.createOrigin = `${displayOrigin} - ${get(salesRecordCreateOriginDeviceConfig, `${originDevice}.label`, '')}`

      // 建立者
      const creator = get(props.data, 'Creator')
      if (creator) {
        data.creatorName = get(creator, 'name')
      }

      const userInfo = get(props.data, 'Member.UserInfo')
      if (userInfo) {
        data.userName = get(userInfo, 'name')
        data.userPhone = get(userInfo, 'phone')
      } else {
        data.userName = '非會員'
      }

      // 建立分店
      data.shopName = get(props.data, 'Shop.name') || '-'

      return data
    })
    const isEzaio = computed(() => {
      return Boolean(find(get(props.data, 'SalesRecordPayments'), { type: 'ezAIOTrust' }))
    })

    const useInvalidPassword = computed(() => get(configData.value, 'useInvalidPassword'))

    // ?? 顯示的服務人員群組商品
    const displaySalesUnitGroupItems = computed(() => {
      const groupedItems = {}

      forEach(get(props.data, 'SalesRecordItems'), item => {
        const salesUnitId = item.SalesUnitId
        if (!groupedItems[salesUnitId]) {
          groupedItems[salesUnitId] = {
            id: salesUnitId,
            name: get(item.SalesUnit, 'name', '無銷售人員'),
            items: [],
          }
        }
        groupedItems[salesUnitId].items.push({
          name: get(item, 'name'),
          totalPrice: get(item, 'totalPrice'),
          id: get(item, 'id'),
          discounts: map(get(item, 'itemDiscounts'), (discount) => {
            return {
              name: get(discount, 'name'),
              savePrice: get(discount, 'savePrice'),
              id: uniqueId(),
            }
          }),
          quantity: get(item, 'quantity'),
        })
      })

      return groupedItems
    })

    // ?? 顯示的整單折扣
    const displayOrderDiscounts = computed(() => {
      const omitType = ['totalItemDiscount']
      const discounts = filter(get(props.data, 'SalesRecordDiscounts', []), (item) => {
        return !includes(omitType, item.type)
      })
      return map(discounts, (item) => {
        return {
          name: item.name,
          savePrice: item.savePrice,
          id: item.id,
        }
      })
    })

    // ?? 顯示的付款方式
    const displayOrderPayments = computed(() => {
      const omitType = ['paidDeposit']
      const payments = filter(get(props.data, 'SalesRecordPayments'), (item) => {
        return !includes(omitType, item.type)
      })

      const extendPaymentOptionsConfig = {
        ...paymentOptionsConfig,
        credit: {
          name: '信用卡',
          value: 'credit',
        },
        other: {
          name: '其他',
          value: 'other',
        },
      }
      extendPaymentOptionsConfig.offline.name = '現場付款'
      extendPaymentOptionsConfig.atm.name = '轉帳'

      const paymentList = map(payments, (item) => {
        const paymentName = get(extendPaymentOptionsConfig, `${item.type}.name`, '其他')
        return {
          name: paymentName,
          amount: item.amount,
          id: item.id,
        }
      })

      const totalPaymentPrice = get(props.data, 'totalPaymentPrice')

      const cashPaid = displayData.value.orderTotalPrice - totalPaymentPrice
      if (cashPaid > 0) {
        paymentList.push({
          name: '現金',
          amount: cashPaid,
          id: 'cash',
        })
      }

      return paymentList
    })

    // ?? 顯示的回饋
    const displayRewards = computed(() => {
      const rewards = get(props.data, 'RewardActivityRecords')
      const oldCashback = get(props.data, 'totalAddCashback')

      const groupedRewards = {}

      forEach(rewards, reward => {
        const rewardType = get(reward, 'rewardType')
        let groupKey = rewardType

        // 如果是點數類型且有 shopPointKey，使用 shopPointKey 作為分組鍵
        if (rewardType === 'POINT') {
          const shopPointKey = get(reward, 'shopPointKey')
          if (shopPointKey) {
            groupKey = `${rewardType}_${shopPointKey}`
          }
        }

        if (!groupedRewards[groupKey]) {
          let name, unitName

          if (rewardType === 'POINT') {
            const shopPointKey = get(reward, 'shopPointKey')
            if (shopPointKey) {
              // 使用 shopPointKey 獲取對應的點數名稱和單位
              name = getShopPointData(shopPointKey, 'name') || '點數'
              unitName = getShopPointData(shopPointKey, 'unitName') || '點'
            } else {
              // 回退到預設點數
              name = get(defaultShopPoint.value, 'name', '點數')
              unitName = get(defaultShopPoint.value, 'unitName', '點')
            }
          } else if (rewardType === 'CASHBACK') {
            name = '檔期回饋金'
          } else {
            name = '其他'
          }

          groupedRewards[groupKey] = {
            rewardType,
            rewardAmount: 0,
            name,
            unitName,
          }
        }

        groupedRewards[groupKey].rewardAmount += get(reward, 'rewardAmount')

        let displayValue
        if (rewardType === 'POINT') {
          displayValue = `${get(groupedRewards[groupKey], 'rewardAmount')} ${get(groupedRewards[groupKey], 'unitName', '點')}`
        } else if (rewardType === 'CASHBACK') {
          displayValue = `$ ${get(groupedRewards[groupKey], 'rewardAmount')}`
        }
        groupedRewards[groupKey].displayValue = displayValue
      })

      if (oldCashback) {
        groupedRewards.oldCashback = {
          rewardType: 'oldCashback',
          rewardAmount: oldCashback,
          name: '回饋金',
          displayValue: `$ ${oldCashback}`,
        }
      }
      return groupedRewards
    })

    const displayAppointmentInfo = computed(() => {
      // 定金、堂票、預約結帳
      const data = {}
      const paidDeposit = filter(get(props.data, 'SalesRecordPayments'), { type: 'paidDeposit' })

      if (paidDeposit.length) {
        const totalDeposit = reduce(paidDeposit, (sum, item) => sum + get(item, 'amount', 0), 0)
        data.deposit = {
          name: '定金',
          amount: totalDeposit,
          displayValue: `$ ${totalDeposit}`,
          type: 'deposit',
        }
      }

      return data
    })

    // 是否為總部店家
    const useHeadquarterShop = computed(() => checkAction('planModule.headquarterShop.enable'))

    // 是否開啟北捷串接模組
    const useTaipeiMetroModule = computed(() => {
      return checkAction('planModule.taipeiMetro.enable')
    })

    // 是否啟用發票功能（檢查電子發票設定、銷售開票開關，若無設定則回退至權限檢查）
    const useInvoiceFeature = computed(() => {
      // 檢查電子發票模組是否啟用
      const invoiceEnabled = get(invoiceConfig.value, 'invoice.enabled')
      if (invoiceEnabled !== undefined && !invoiceEnabled) {
        return false
      }
      // 檢查銷售開票開關（從 SalesInvoiceConfig 取得）
      const posInvoiceEnabled = get(salesConfig.value, 'posInvoiceEnabled')
      if (posInvoiceEnabled !== undefined) {
        return Boolean(posInvoiceEnabled)
      }
      return checkAction('admin.salesRecord.issueInvoice')
    })

    // 捷運點點數名稱與單位
    const taipeiMetroPoint = computed(() => {
      const taipeiMetroPoint = find(shopPointList.value, point => isTaipeiMetroPoint(point))
      if (taipeiMetroPoint) {
        return {
          name: get(taipeiMetroPoint, 'name', '捷運點'),
          unitName: get(taipeiMetroPoint, 'unitName', '點'),
        }
      }
      return {
        name: '捷運點',
        unitName: '點',
      }
    })

    // 北捷綁定狀態 (從 API 取得)
    const taipeiMetroBindingStatus = ref(null)

    // 捷運點發放紀錄顯示狀態
    const eventAwardDisplayStatus = computed(() => {
      // 檢查會員是否完成綁定 (使用 API 回傳的狀態)
      // 綁定成功條件: isAppBindLine === true OR MemberAuthLine 存在
      const isAppBindLine = get(taipeiMetroBindingStatus.value, 'isAppBindLine')
      const memberAuthLine = get(taipeiMetroBindingStatus.value, 'MemberAuthLine')
      const isBound = isAppBindLine || memberAuthLine !== null

      if (!isBound) {
        return {
          showRecords: false,
          showHintMessage: true,
          hintMessage: '未完成綁定,不符合贈點資格',
          hintTextColor: 'text-danger',
        }
      }

      // 檢查是否有獎勵紀錄
      const hasRecords = eventPlaybookAwardRecords.value.length > 0

      if (!hasRecords) {
        return {
          showRecords: false,
          showHintMessage: true,
          hintMessage: '無事件行銷發放紀錄',
          hintTextColor: 'text-gray-60',
        }
      }

      return {
        showRecords: true,
        showHintMessage: false,
        hintMessage: '',
        hintTextColor: '',
      }
    })

    // 格式化狀態顯示
    function formatStatus (status) {
      return get(sendStatusConfig, [status, 'label'], status)
    }

    // 根據狀態生成顯示欄位
    const generateDisplayFields = (record) => {
      const status = record.status
      const fields = [
        {
          label: '發送狀態',
          prop: 'status',
          body: {
            render: () => formatStatus(status),
          },
        },
        {
          label: '活動名稱',
          prop: 'eventName',
          body: {
            render: () => get(record, 'EventPlaybook.name', '-'),
          },
        },
        {
          label: '發送點數',
          prop: 'sendAmount',
          body: {
            render: () => {
              if (['pending', 'waiting', 'sending'].includes(status)) {
                return '-'
              }
              const shopPointKey = record.shopPointKey
              const unitName = shopPointKey ? getShopPointData(shopPointKey, 'unitName') || '點' : '點'
              return `${record.awardAmount || 0} ${unitName}`
            },
          },
        },
        {
          label: '發送時間',
          prop: 'sendTime',
          body: {
            render: () => {
              if (!record.sendAt) {
                return '-'
              }
              return formatDate(record.sendAt, 'YYYY/MM/DD HH:mm:ss')
            },
          },
        },
        {
          label: '回收點數',
          prop: 'revokeAmount',
          body: {
            render: () => {
              if (status === 'revoking' || status === 'revokeFailed') return '-'
              const shopPointKey = record.shopPointKey
              const unitName = shopPointKey ? getShopPointData(shopPointKey, 'unitName') || '點' : '點'
              return `${record.revokeAmount || 0} ${unitName}`
            },
          },
        },
        {
          label: '回收時間',
          prop: 'revokeTime',
          body: {
            render: () => {
              if (status === 'revokeFailed' || !record.revokedAt) return '-'
              return formatDate(record.revokedAt, 'YYYY/MM/DD HH:mm:ss')
            },
          },
        },
        {
          label: '備註',
          prop: 'awardNote',
          body: {
            render: () => record.awardNote || '',
          },
        },
        {
          label: '備註',
          prop: 'revokeNote',
          body: {
            render: () => {
              const isPartialRevoke = status === 'revoked' && record.revokeAmount < record.awardAmount
              const noteText = record.revokeNote || ''
              if (isPartialRevoke) {
                return noteText ? `${noteText} (部分回收)` : '(部分回收)'
              }
              return noteText
            },
          },
        },
      ]

      const omitFields = []
      const showRevokeInfo = ['revoking', 'revoked', 'revokeFailed'].includes(status)
      const showAwardNote = (status === 'failed' || status === 'sent') && record.awardNote
      const isPartialRevoke = status === 'revoked' && record.revokeAmount < record.awardAmount
      const showRevokeNote = (status === 'revokeFailed' && record.revokeNote) || (status === 'revoked' && (record.revokeNote || isPartialRevoke))

      if (!showRevokeInfo) {
        omitFields.push('revokeAmount', 'revokeTime')
      }
      if (!showAwardNote) {
        omitFields.push('awardNote')
      }
      if (!showRevokeNote) {
        omitFields.push('revokeNote')
      }

      return filter(fields, (field) => (!omitFields.includes(field.prop)))
    }

    // 獲取會員北捷綁定狀態
    async function fetchTaipeiMetroBindingStatus () {
      const phone = get(props.data, 'Member.phone')
      if (!phone || !useTaipeiMetroModule.value) {
        return
      }

      const [res, errMsg] = await GetMemberTaipeiMetroMissionStatus({
        shopId: shopId.value,
        phone,
      })

      if (errMsg) {
        // 綁定狀態查詢失敗，預設為未綁定
        taipeiMetroBindingStatus.value = {
          isAppBindLine: false,
          MemberAuthLine: null,
        }
        return
      }

      taipeiMetroBindingStatus.value = res || {
        isAppBindLine: false,
        MemberAuthLine: null,
      }
    }

    // 載入事件行銷獎勵紀錄
    async function loadEventPlaybookAwardRecords () {
      // 檢查是否為總部店家並且開啟北捷模組
      if (!useHeadquarterShop.value || !useTaipeiMetroModule.value) return

      const salesRecordId = get(props.data, 'id')
      if (!salesRecordId) return

      const [res, err] = await FindOrgEventPlaybookAwardRecord({
        shopId: shopId.value,
        id: salesRecordId,
      })

      if (err) {
        window.$message.error(err)
        return
      }

      // 僅篩選捷運點類型的獎勵
      eventPlaybookAwardRecords.value = filter(
        get(res, 'eventPlaybookAwardRecords', []),
        record => {
          if (record.awardType !== 'point') return false
          const point = find(shopPointList.value, { key: record.shopPointKey })
          return isTaipeiMetroPoint(point)
        },
      )
    }

    // !! 顯示的新增權益
    const displayAddBenefits = computed(() => {
      return formatBenefitChangeLogs(get(props.data, 'changeItemLogs'), 'add', {
        shopPointList: shopPointList.value,
      })
    })

    // !! 顯示的使用權益
    const displayUsedBenefits = computed(() => {
      return formatBenefitChangeLogs(get(props.data, 'changeItemLogs'), 'spend', {
        shopPointList: shopPointList.value,
      })
    })

    const showControl = computed(() => {
      const controls = {
        block: {
          orderDiscount: false,
          rewards: false,
          qrcode: false,
          appointmentInfo: false,
          addBenefits: false,
          usedBenefits: false,
          invoiceInfo: false,
          eventAward: useHeadquarterShop.value && useTaipeiMetroModule.value,
        },
        btn: {
          createNote: false,
          editNote: false,
          cancel: false, // 返回按鈕
          void: false, // 作廢按鈕
        },
        paymentQRcode: false,
        qrcodeInvalid: false,
      }

      if (displayAddBenefits.value.length) {
        controls.block.addBenefits = true
      }

      if (displayUsedBenefits.value.length) {
        controls.block.usedBenefits = true
      }

      if (displayOrderDiscounts.value.length > 0) {
        controls.block.orderDiscount = true
      }

      if (!isEmpty(displayRewards.value)) {
        controls.block.rewards = true
      }

      if (displayData.value.note) {
        controls.btn.createNote = false
        controls.btn.editNote = !props.isOrg
      } else {
        controls.btn.createNote = !props.isOrg
        controls.btn.editNote = false
      }

      const paymentOrderStatus = get(props.data, 'SalesPaymentOrder.status')
      if (paymentOrderStatus === 'pending') {
        controls.block.qrcode = true
        controls.paymentQRcode = true
      } else if (paymentOrderStatus === 'fail') {
        controls.block.qrcode = true
        controls.qrcodeInvalid = true
      }

      if (!isEzaio.value && ['pending', 'complete'].includes(get(props.data, 'status'))) {
        controls.btn.void = !props.isOrg
      }

      if (!isEmpty(displayAppointmentInfo.value)) {
        controls.block.appointmentInfo = true
      }

      if (get(props.data, 'id') && useInvoiceFeature.value) {
        controls.block.invoiceInfo = true
      }

      return controls
    })

    const onOrderVoid = () => {
      modal.void = true
    }

    const onRefresh = () => {
      emit('refresh')
    }

    const onVoidRefresh = () => {
      emit('close')
      emit('refresh')
    }

    onBeforeMount(async () => {
      await Promise.all([
        fetchTaipeiMetroBindingStatus(),
        loadEventPlaybookAwardRecords(),
        fetchInvoiceConfig(),
        fetchSalesConfig(),
      ])
      setTimeout(() => {
        show.value = true
      }, 100)
    })

    onBeforeUnmount(() => {
      show.value = false
    })

    return {
      modal,
      displayData,
      showControl,
      show,
      displaySalesUnitGroupItems,
      displayOrderDiscounts,
      displayOrderPayments,
      displayRewards,
      paymentQrcode,
      onRefresh,
      onVoidRefresh,
      onOrderVoid,
      configData,
      displayAppointmentInfo,
      displayAddBenefits,
      displayUsedBenefits,
      eventPlaybookAwardRecords,
      taipeiMetroPoint,
      eventAwardDisplayStatus,
      taipeiMetroBindingStatus,
      generateDisplayFields,
    }
  },
})
</script>

<style lang="postcss" scoped>
.row {
  @apply flex items-start justify-between gap-[8px];
}

.sales-unit {
  @apply bg-primary-30 py-[4.5px] px-[10px];
}

.sales-unit-group {
  @apply flex flex-col gap-[18px] mt-[16px];
}

.content-wrapper {
  @apply px-[36px];
}
</style>
